import { useMemo } from "react";

import { useProductLoyaltyQuery } from "@jti/rest-api";
import type { GetEligibleCampaignsResponseType } from "@jti/rest-api-types";

import {
  getHighestEligibleCampaign,
  type LoyaltyDiscountState,
  resolveLoyaltyDiscountState,
} from "./loyaltyDiscountState";
import type { LoyaltyPriceTagState } from "./ProductLoyaltyBlock.types";

type LoyaltyUser = {
  uuid: string;
  ciam_token: string;
};

type HighestTier = {
  points: number;
  price: number;
};

type LoyaltyQueryData = {
  points: number;
  timeLimited: number;
  loyaltyDiscountState: LoyaltyDiscountState;
  highestTier: HighestTier | null;
  priceTagState: LoyaltyPriceTagState;
};

type UseLoyaltyProductDataParams = {
  sku: string;
  selectedQuantity?: number;
  unitPrice?: number | null;
  isLoyaltyActive: boolean;
  isLoyaltyRestricted: boolean;
  isRewardCopyAuthoringEnabled: boolean;
};

type UseLoyaltyProductDataResult = LoyaltyQueryData & {
  isLoaded: boolean;
};

const isLoyaltyUser = (value: unknown): value is LoyaltyUser =>
  value !== null &&
  typeof value === "object" &&
  "uuid" in value &&
  typeof value.uuid === "string" &&
  "ciam_token" in value &&
  typeof value.ciam_token === "string";

const getUserFromStorage = (): LoyaltyUser | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const user = window.localStorage.getItem("user");

    if (!user) {
      return null;
    }

    const parsedUser = JSON.parse(user);

    if (!isLoyaltyUser(parsedUser)) {
      return null;
    }

    return {
      uuid: parsedUser.uuid,
      ciam_token: parsedUser.ciam_token,
    };
  } catch {
    return null;
  }
};

const resolvePriceTagState = (
  discountState: LoyaltyDiscountState,
  highestEligibleCampaign: GetEligibleCampaignsResponseType | null
): LoyaltyPriceTagState => {
  if (discountState === "anonymous") {
    return { status: "anonymous" };
  }

  if (discountState === "logged-in-partial-discount" && highestEligibleCampaign) {
    return {
      status: "partial",
      discountAmount: highestEligibleCampaign.price,
      discountPoints: highestEligibleCampaign.points,
    };
  }

  return { status: "hidden" };
};

const DEFAULT_QUERY_DATA: LoyaltyQueryData = {
  points: 0,
  timeLimited: 0,
  loyaltyDiscountState: "anonymous",
  highestTier: null,
  priceTagState: { status: "hidden" },
};

export const useLoyaltyProductData = ({
  sku,
  selectedQuantity,
  unitPrice,
  isLoyaltyActive,
  isLoyaltyRestricted,
}: UseLoyaltyProductDataParams): UseLoyaltyProductDataResult => {
  const user = getUserFromStorage();
  const productQuantity = selectedQuantity ?? 1;
  const isLoggedIn = Boolean(user?.uuid && user?.ciam_token);

  const shouldHideLoyalty = isLoyaltyRestricted || (isLoggedIn && !isLoyaltyActive);
  const shouldFetchLoyaltyData = Boolean(sku) && !shouldHideLoyalty;
  const loggedInUser = isLoggedIn && user ? { uuid: user.uuid, ciamToken: user.ciam_token } : undefined;

  const loyaltyQuery = useProductLoyaltyQuery({
    sku,
    quantity: productQuantity,
    unitPrice,
    user: loggedInUser,
    enabled: shouldFetchLoyaltyData,
  });

  return useMemo(() => {
    if (shouldHideLoyalty) {
      return {
        ...DEFAULT_QUERY_DATA,
        isLoaded: true,
      };
    }

    if (loyaltyQuery.data) {
      const { productPoints, eligibleCampaigns } = loyaltyQuery.data;
      const loyaltyDiscountState = resolveLoyaltyDiscountState(isLoggedIn, eligibleCampaigns);
      const highestEligibleCampaign = getHighestEligibleCampaign(eligibleCampaigns);

      return {
        points: productPoints?.points ?? 0,
        timeLimited:
          productPoints?.time_limited && productPoints.time_to ? new Date(productPoints.time_to).getTime() : 0,
        loyaltyDiscountState,
        highestTier: highestEligibleCampaign
          ? {
              points: highestEligibleCampaign.points,
              price: highestEligibleCampaign.price,
            }
          : null,
        priceTagState: resolvePriceTagState(loyaltyDiscountState, highestEligibleCampaign),
        isLoaded: true,
      };
    }

    return {
      ...DEFAULT_QUERY_DATA,
      isLoaded: loyaltyQuery.isFetched,
    };
  }, [isLoggedIn, loyaltyQuery.data, loyaltyQuery.isFetched, shouldHideLoyalty]);
};
